import "./index.css";
import { useParams, Link, useNavigate } from "react-router-dom";
import React from "react";

import Chat from "../components/chat";
import NewChatButton from "../components/newchatbutton";
import PDFReader from "../components/pdfreader";
import { MsgSVG, Trash } from "../components/icons";

import { ajax } from "../utils/request";
import { message, Popconfirm } from "antd";
import { saveSiderToForage, getSiderFromForage, getToken } from "../utils/storage";
import { useTranslation } from "react-i18next";
import Account from "../components/pricing";
import Register from "../components/register";
import Login from "../components/login";
import FAQ from "../components/faq";

// Icône hamburger
const HamburgerIcon = ({ open }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    {open ? (
      // Croix
      <path d="M3 6H21M3 12H21M3 18H21" stroke="black" strokeWidth="2" strokeLinecap="round"/>
    ) : (
      // Hamburger
      <path d="M3 6H21M3 12H21M3 18H21" stroke="black" strokeWidth="2" strokeLinecap="round"/>
    )}
  </svg>
);

export default function Work() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showOverlay, setShowOverlay] = React.useState(true);
  const [windowHeight, setWindowHeight] = React.useState(0);
  const [messageApi, contextHolder] = message.useMessage();

  const [resetSignal, setResetSignal] = React.useState(0);
  const [chatList, setChatList] = React.useState([]);
  const { chatId } = useParams();

  const [currentLoadedChatData, setCurrentLoadedChatData] = React.useState(null);
  const [isLogin, setIsLogin] = React.useState(false);
  const [accountOpen, setAccountOpen] = React.useState(false);
  const [loginOpen, setLoginOpen] = React.useState(false);
  const [registerOpen, setRegisterOpen] = React.useState(false);
  const [faqOpen, setFaqOpen] = React.useState(false);

  const [activePdfUrl, setActivePdfUrl] = React.useState(null);

  // Sidebar ouverte/fermée
  const [sidebarOpen, setSidebarOpen] = React.useState(true);

  React.useEffect(() => {
    setWindowHeight(window.innerHeight);
    getToken().then((token) => {
      if (token) {
        setIsLogin(true);
        fetchUserChats();
      }
    });
  }, []);

  React.useEffect(() => {
    if (chatId) {
      loadChatFullData(chatId);
      setShowOverlay(false);
    } else {
      setCurrentLoadedChatData(null);
      setShowOverlay(true);
      setActivePdfUrl(null);
      setResetSignal(prev => prev + 1);
    }
  }, [chatId]);

  const fetchUserChats = async () => {
    try {
      const res = await ajax.get('/chat/chats');
      if (res.data && res.data.data) {
        setChatList(res.data.data);
        saveSiderToForage(res.data.data);
      }
    } catch (error) {
      messageApi.error("Failed to load chat history.");
    }
  };

  const loadChatFullData = async (id) => {
    try {
      const res = await ajax.get(`/chat/chats/${id}`);
      if (res.data && res.data.data) {
        setCurrentLoadedChatData(res.data.data);
      }
    } catch (error) {
      messageApi.error("Failed to load chat messages.");
      if (error.response && (error.response.status === 404 || error.response.status === 403)) {
        navigate('/c/start');
      }
    }
  };

  const handleDeleteChat = async (id) => {
    try {
      const res = await ajax.delete(`/chat/chats/${id}`);
      if (res.data && res.data.msg === "Chat deleted successfully") {
        messageApi.success("Chat deleted successfully.");
        fetchUserChats();
        if (chatId && chatId.toString() === id.toString()) {
          handleNewChat();
        }
      } else {
        messageApi.error("Failed to delete chat.");
      }
    } catch (error) {
      messageApi.error("Failed to delete chat.");
    }
  };

  const handleNewChat = () => {
    navigate('/c/start');
  };

  const handleQuerySubmit = (newChatId) => {
    setShowOverlay(false);
    if (newChatId && newChatId.toString() !== chatId) {
      navigate(`/c/${newChatId}`);
    }
    fetchUserChats();
    if (chatId) {
      loadChatFullData(chatId);
    }
  };

  const handleExtractPage = (urlWithPage) => {
    setActivePdfUrl(urlWithPage);
  };

  return (
    <div className="main h-full w-full">
      {contextHolder}

      <div className="wrapper flex flex-row">
        <Register isOpen={registerOpen} setOpen={setRegisterOpen} setLoginOpen={setLoginOpen} />
        <Login isOpen={loginOpen} setOpen={setLoginOpen} setRegisterOpen={setRegisterOpen} setIsLogin={setIsLogin} />
        <Account isOpen={accountOpen} setOpen={setAccountOpen} />
        <FAQ isOpen={faqOpen} setOpen={setFaqOpen} />

        {/* SIDEBAR */}
        <div
          className={`sider flex flex-col bg-[#f5f5f7] transition-all duration-300`}
          style={{ width: sidebarOpen ? "256px" : "60px", height: windowHeight }}
        >
          {/* Header avec bouton toggle */}
          <div className="sider-header flex items-center justify-between mx-[8px] my-[8px]">
            {sidebarOpen && (
              <a href="/" className="sider-title flex items-center gap-2 font-bold text-black hover:opacity-80 transition-opacity">
                <img src="/images/Logo CiviChat.png" alt="CiviChat" className="h-[45px]" />
              </a>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="toggle-sidebar p-2 rounded-md hover:bg-gray-200"
            >
              <HamburgerIcon open={sidebarOpen} />
            </button>
          </div>

          {/* Bouton NewChat - toujours visible */}
          {sidebarOpen ? (
            <NewChatButton onNewChat={handleNewChat} />
          ) : (
            <div className="flex justify-center mx-[8px] mb-[8px]">
              <button
                onClick={handleNewChat}
                className="p-2 rounded-md hover:bg-gray-200 transition-colors"
                title="New Chat"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>
          )}

          {/* Liste de chats - visible seulement si sidebar ouverte */}
          {sidebarOpen && (
            <div className="sider-scroll-area">
              <div className="pdf-list flex flex-col my-[8px]">
                {chatList && chatList.map((item) => {
                  const isActive = item.id.toString() === chatId;
                  const chatDate = new Date(item.update_time).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  });

                  return (
                    <Link
                      key={item.id}
                      to={`/c/${item.id}`}
                      className={`${isActive ? "active" : ""} chat-item rounded-md text-[14px] flex flex-row items-center h-[40px] flex-grow group`}
                    >
                      <span>
                        <MsgSVG fill={`${isActive ? '#fff' : "#000"}`} size="18" />
                      </span>
                      <span className="whitespace-nowrap text-ellipsis overflow-hidden ml-[5px]">
                        {item.title}
                      </span>
                      <span className="ml-auto text-[12px] opacity-70">
                        {chatDate}
                      </span>
                      <Popconfirm
                        title="Delete the chat"
                        description="Are you sure to delete this chat?"
                        onConfirm={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteChat(item.id);
                        }}
                        okText="Yes"
                        cancelText="No"
                      >
                        <button
                          className="delete-chat-button opacity-0 group-hover:opacity-100 transition-opacity ml-2"
                          style={{ background: 'none', border: 'none', padding: 0 }}
                        >
                          <Trash size={18} fill="#969696" />
                        </button>
                      </Popconfirm>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Footer : complet si ouvert, mini-logo si fermé */}
          {sidebarOpen ? (
            <div className="sider-footer text-[#969696] flex gap-[9px] px-[8px] text-[14px] hover:text-[var(--blue-text)] cursor-pointer">
              <a className="text-[#969696] text-[14px]" href="/">
                {t("index")}
              </a>
              <div
                className="text-[#969696] hover:text-[var(--blue-text)] transition cursor-pointer"
                onClick={() => setFaqOpen(true)}
              >
                FAQ
              </div>
            </div>
          ) : (
            <div className="sider-footer-closed flex justify-center items-center pb-3">
              <a href="/" className="sidebar-mini-logo">
                <img src="/images/logo.png" alt="CiviBOT" className="h-[48px] w-auto opacity-80 hover:opacity-100 transition" />
              </a>
            </div>
          )}
        </div>

        {/* CONTENT */}
        <div
          className="content flex flex-row transition-all duration-300"
          style={{ width: sidebarOpen ? "calc(100vw - 256px)" : "calc(100vw - 60px)" }}
        >
          {activePdfUrl ? (
            <>
              <div className="flex-[4] bg-white flex justify-center">
                <Chat
                  setPdfLink={setActivePdfUrl}
                  onQuerySubmit={handleQuerySubmit}
                  resetSignal={resetSignal}
                  showOverlay={showOverlay}
                  setShowOverlay={setShowOverlay}
                  currentChatId={chatId}
                  currentChatMessages={currentLoadedChatData}
                />
              </div>
              <div className="flex-[6] bg-white flex flex-col">
                <div className="pdf-reader h-full">
                  <PDFReader pdfUrl={activePdfUrl} />
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 bg-white flex justify-center">
              <Chat
                setPdfLink={setActivePdfUrl}
                onQuerySubmit={handleQuerySubmit}
                resetSignal={resetSignal}
                showOverlay={showOverlay}
                setShowOverlay={setShowOverlay}
                currentChatId={chatId}
                currentChatMessages={currentLoadedChatData}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}